import React, {useEffect, useState} from 'react'
import Button from 'lib/ui/Button'
import Dialog from 'lib/ui/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import styled from 'styled-components'
import CompletedTestForm from 'organization/Event/Form/WebhookTest/CompletedTestForm'
import {Form} from 'organization/Event/FormsProvider'
import {Answer} from 'Event/SubmissionsProvider'
import {FormSubmission} from 'organization/Event/Form/FormModeration/Question'
import {useOrganization} from 'organization/OrganizationProvider'
import {api} from 'lib/url'
import {useQueryClient} from 'react-query'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'

export default function EditSubmissionDialog(props: {
  onClose: () => void
  form: Form
  submission: FormSubmission | null
}) {
  const {submission, onClose, form} = props
  const [loading, setLoading] = useState<boolean>(false)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()

  const update = useUpdateAnswers()

  useEffect(() => {
    if (submission) {
      setAnswers(convertAnswers(submission, form))
    }
  }, [submission, form])

  const editSubmission = () => {
    setError(null)
    setLoading(true)

    const filledAnswers = answers.map((answer) => {
      if (answer.value === undefined) {
        answer.value = ''
      }

      return answer
    })

    const updateData: UpdateData = {
      submission_id: submission?.id,
      form_id: form.id,
      answers: filledAnswers,
    }

    update(updateData)
      .then(() => {
        qc.invalidateQueries([
          'forms',
          form.id,
          'submissions',
          {status: submission?.status},
        ])
        onClose()
      })
      .catch((e) => {
        setError(e.message)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Dialog
      open={submission !== null}
      onClose={onClose}
      PaperProps={{
        style: {
          width: '100%',
          overflow: 'initial',
        },
      }}
    >
      <DialogTitle>Edit submission</DialogTitle>
      <DialogContent>
        <CompletedTestForm
          form={form}
          answers={answers}
          setAnswers={setAnswers}
        />
        <ErrorAlert>{error}</ErrorAlert>

        <StyledButton
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={editSubmission}
          aria-label="confirm edit submission"
        >
          Edit submission
        </StyledButton>
      </DialogContent>
    </Dialog>
  )
}

const convertAnswers = (
  submission: FormSubmission | null,
  form: Form,
): Answer[] => {
  if (!submission) {
    return []
  }

  return submission.answers.map((answer) => ({
    question_id: answer.question_id,
    value: answer.answer,
    id: submission.id,
    form_id: form.id,
  }))
}

type UpdateData = {
  form_id: number
  submission_id?: string
  answers: Answer[]
}

export function useUpdateAnswers() {
  const {client} = useOrganization()

  return async (data: UpdateData): Promise<void> => {
    const url = api(`/forms/${data.form_id}/submissions`)

    return await client.put(url, data)
  }
}

const StyledButton = styled(Button)`
  height: 32px;
  align-self: center;
  font-size: 11px;
  margin-bottom: ${(props) => props.theme.spacing[4]};
`
